// DERIVADO DE: gestion-web-cliente@1a5ce2d39
//   src/app/auxiliares/servicios/cliente-jerarquia.service.ts
// CAMBIOS: el índice es un `resource` que se recarga al cambiar de permiso, en
//   vez de una caché de cinco minutos; la cadena y la ruta salen de las
//   funciones puras de `jerarquia.ts`.

import { Service, computed, inject, resource } from '@angular/core';
import type { ICliente } from 'modelos';

import { SesionStore } from '@core/sesion/sesion.store';
import { ClientesHttp } from '@compartido/datos/catalogos.http';
import { cadena, indexar, ruta } from './jerarquia';

/**
 * Los clientes visibles, indexados una sola vez para toda la aplicación.
 *
 * Es lo que usan las columnas "Cliente" de los listados para mostrar de qué
 * cliente padre viene cada uno: la tabla pregunta por celda y en cada change
 * detection, así que todo es una lectura del índice en memoria, nunca un pedido.
 *
 * Una sola consulta con los dos campos que hacen falta (`nombre` e `idPadre`).
 * El backend sólo devuelve la jerarquía del permiso actual, así que la cadena
 * se corta sola en el cliente propio; si algún cliente padre no es visible, se
 * corta ahí, sin error.
 */
@Service()
export class JerarquiaClientesStore {
  private readonly http = inject(ClientesHttp);
  private readonly sesion = inject(SesionStore);

  private readonly clientes = resource({
    // Con otro permiso la jerarquía visible es otra. Espera al permiso, no sólo
    // al token: al entrar hay un momento con token y sin permiso elegido, y el
    // pedido salía con `x-permiso` vacío → 403 «Forbidden resource» en pantalla.
    params: () =>
      this.sesion.logueado() && this.sesion.idPermiso()
        ? { permiso: this.sesion.idPermiso() }
        : undefined,
    loader: () =>
      this.http.listar({ select: 'nombre idPadre', unlimited: true, includeChildren: true }),
  });

  // `hasValue()` antes de `value()`: en error, `value()` tira y rompe la pantalla.
  readonly indice = computed(() =>
    indexar(this.clientes.hasValue() ? (this.clientes.value()?.datos ?? []) : []),
  );

  /**
   * Si el permiso ve algún cliente además del propio; `undefined` mientras no
   * se sabe. Sin hijos, pedir `includeChildren` no suma nada y sí cuesta: suma
   * al scope la rama `idsAncestros`, que en `eventogenericos` no tiene índice y
   * tira el conteo al `maxTimeMS` (500).
   */
  readonly tieneHijos = computed<boolean | undefined>(() => {
    if (!this.clientes.hasValue()) return this.clientes.error() ? true : undefined;
    const propio = this.sesion.permisoActual()?.idCliente;
    return (this.clientes.value()?.datos ?? []).some((c) => c._id !== propio);
  });

  /** Del cliente más alto visible hasta este, incluido. */
  cadena(id: string | undefined): ICliente[] {
    return cadena(this.indice(), id);
  }

  /** `Intendencia ▸ Zona norte ▸ Centro`. Vacío si no tiene cliente padre visible. */
  ruta(id: string | undefined): string {
    return ruta(this.indice(), id);
  }

  /** El nombre según el índice, para las filas que traen sólo el id. */
  nombre(id: string | undefined): string | undefined {
    return id ? this.indice().get(id)?.nombre : undefined;
  }
}
